import {Component, EventEmitter, Input, OnInit, Output} from "@angular/core";

@Component({
  selector: 'image-crop',
  templateUrl: './image-crop.component.html',
  styleUrls: ['./image.component.css']
})
export class ImageCropComponent implements OnInit {
  public croppingImageIndex: number = 0;
  public imagesData: Array<{original: string, display: string, cropped: string, fileName: string,
    fileType: string, file: File, width: number, height: number, sourceWidth: number,
    scale: number, rect: any, fullFrame: boolean, opaque: boolean}> = [];

  private displayMaxWidth = 900;
  private displayMaxHeight = 460;

  @Input('cropperDimensions') cropperMaxDimensions = {
    width: 400,
    height: 400
  }
  @Input('images') images: File[] = [];
  @Input('maintainAspectRatio') maintainAspectRatio: boolean = true;
  @Input('aspectRatio') aspectRatio: number = 1;
  @Output('onImageCropped') onImagesCropped: EventEmitter<File[]> = new EventEmitter<File[]>();
  @Output('cancel') onCancel: EventEmitter<any> = new EventEmitter<any>();

  async ngOnInit() {
    let images = []
    for (const image of this.images) {
      if (image) {
        let imageData = {}
        let dataUrl = await this.fileToBase64(image)
        const size = await this.imageSize(dataUrl);
        const display = await this.scaleToFit(dataUrl, size);
        imageData['original'] = dataUrl;
        imageData['display'] = display.dataUrl;
        imageData['cropped'] = display.dataUrl;
        imageData['fileName'] = image.name
        imageData['fileType'] = image.type
        imageData['file'] = image
        imageData['width'] = display.width
        imageData['height'] = display.height
        imageData['sourceWidth'] = size.width
        imageData['scale'] = display.scale
        imageData['rect'] = null
        imageData['fullFrame'] = true
        imageData['opaque'] = this.isOpaque(dataUrl)
        images.push(imageData)
      }
    }
    if (images.length > 0) {
      this.imagesData = images
    }
  }

  imageCropped(event) {
    let currentImageData = this.imagesData[this.croppingImageIndex];
    currentImageData['cropped'] = event.base64;
    currentImageData['rect'] = event.imagePosition;
    currentImageData['fullFrame'] = this.isFullFrame(event, currentImageData);
    this.imagesData[this.croppingImageIndex] = currentImageData;
  }

  // imagePosition is the crop rect in source pixels.
  private isFullFrame(event, data): boolean {
    let full = false;
    const rect = event.imagePosition;
    if (rect && data.width && data.height) {
      full = rect.x1 <= 1 && rect.y1 <= 1 &&
        rect.x2 >= data.width - 1 && rect.y2 >= data.height - 1;
    }
    return full;
  }

  // PNG output for a JPEG source inflates it badly, so follow the source format.
  cropFormat(image?): string {
    let format = 'png';
    let data = image;
    if (!data) {
      data = this.imagesData[this.croppingImageIndex];
    }
    if (data) {
      if (data.fileType === 'image/jpeg' || data.fileType === 'image/jpg') {
        format = 'jpeg';
      } else if (data.fileType === 'image/webp') {
        format = 'webp';
      }
    }
    return format;
  }

  async saveImages() {
    let images: File[] = [];
    for (const data of this.imagesData) {
      let imageFile = data.file;
      const cap = this.cropperMaxDimensions.width;
      const oversized = cap > 0 && data.sourceWidth > cap;
      if (!data.fullFrame || oversized) {
        const cropped = await this.cropFromOriginal(data);
        imageFile = await this.base64TOFile(cropped, data.fileName, data.fileType);
        if (imageFile.size > data.file.size && data.opaque) {
          const jpeg = await this.reEncodeAsJpeg(cropped);
          const jpegFile = await this.base64TOFile(jpeg, this.jpegFileName(data.fileName), 'image/jpeg');
          if (jpegFile.size < imageFile.size) {
            imageFile = jpegFile;
          }
        }
      }
      images.push(imageFile);
    }

    if (images.length > 0) {
      this.onImagesCropped.emit(images)
    }
  }

  changeIndex(index: number) {
    this.croppingImageIndex = index;
  }

  restoreToOriginal() {
    let currentlyWorkingImage = this.imagesData[this.croppingImageIndex];
    currentlyWorkingImage.cropped = currentlyWorkingImage.display;
    currentlyWorkingImage.rect = null;
    currentlyWorkingImage.fullFrame = true;
    this.imagesData[this.croppingImageIndex] = currentlyWorkingImage;
  }

  async cancel() {
    let images: File[] = [];
    for (const data of this.imagesData) {
      images.push(data.file);
    }

    if (images.length > 0) {
      this.onImagesCropped.emit(images)
    }
  }

  fileToBase64(file: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.toString());
      reader.onerror = error => reject(error);
    })
  }

  // Crop out of the full-resolution original, not the display copy.
  private cropFromOriginal(data): Promise<string> {
    return new Promise<string>((resolve) => {
      const img = new Image();
      img.onload = () => {
        let rect = data.rect;
        if (!rect) {
          rect = {x1: 0, y1: 0, x2: data.width, y2: data.height};
        }
        const sx = Math.round(rect.x1 * data.scale);
        const sy = Math.round(rect.y1 * data.scale);
        const sw = Math.round((rect.x2 - rect.x1) * data.scale);
        const sh = Math.round((rect.y2 - rect.y1) * data.scale);
        let outWidth = sw;
        let outHeight = sh;
        const cap = this.cropperMaxDimensions.width;
        if (cap > 0 && outWidth > cap) {
          outHeight = Math.round(sh * cap / sw);
          outWidth = cap;
        }
        const canvas = document.createElement('canvas');
        canvas.width = outWidth;
        canvas.height = outHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outWidth, outHeight);
        resolve(canvas.toDataURL('image/' + this.cropFormat(data), 0.92));
      };
      img.onerror = () => resolve(data.cropped);
      img.src = data.original;
    })
  }

  // scale maps display pixels back to original pixels.
  private scaleToFit(dataUrl: string, size): Promise<{dataUrl: string, width: number, height: number, scale: number}> {
    return new Promise<{dataUrl: string, width: number, height: number, scale: number}>((resolve) => {
      let ratio = 1;
      if (size.width > this.displayMaxWidth) {
        ratio = this.displayMaxWidth / size.width;
      }
      if (size.height * ratio > this.displayMaxHeight) {
        ratio = this.displayMaxHeight / size.height;
      }
      if (ratio >= 1) {
        resolve({dataUrl: dataUrl, width: size.width, height: size.height, scale: 1});
      } else {
        const img = new Image();
        img.onload = () => {
          const width = Math.round(size.width * ratio);
          const height = Math.round(size.height * ratio);
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          resolve({dataUrl: canvas.toDataURL('image/png'), width: width, height: height, scale: size.width / width});
        };
        img.onerror = () => resolve({dataUrl: dataUrl, width: size.width, height: size.height, scale: 1});
        img.src = dataUrl;
      }
    })
  }

  imageSize(dataUrl: string): Promise<{width: number, height: number}> {
    return new Promise<{width: number, height: number}>((resolve) => {
      const img = new Image();
      img.onload = () => resolve({width: img.naturalWidth, height: img.naturalHeight});
      img.onerror = () => resolve({width: 0, height: 0});
      img.src = dataUrl;
    })
  }

  private isOpaque(dataUrl: string): boolean {
    let opaque = true;
    if (dataUrl.indexOf('data:image/png') === 0) {
      const header = atob(dataUrl.split(',')[1].substring(0, 44));
      const colorType = header.charCodeAt(25);
      opaque = colorType !== 4 && colorType !== 6;
    }
    return opaque;
  }

  private jpegFileName(fileName: string): string {
    let name = fileName;
    const dot = fileName.lastIndexOf('.');
    if (dot > 0) {
      name = fileName.substring(0, dot);
    }
    return name + '.jpg';
  }

  private reEncodeAsJpeg(dataUrl: string): Promise<string> {
    return new Promise<string>((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/jpeg', 0.92));
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    })
  }

  async base64TOFile(dataUrl, fileName, fileType): Promise<File> {
    const res: Response = await fetch(dataUrl);
    const blob: Blob = await res.blob();
    const type = blob.type ? blob.type : fileType;
    return new File([blob], fileName, {lastModified: new Date().getDate(), type: type});
  }
}
